import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReviewKind } from '../../shared/progress';
import {
  CANVAS_H,
  CANVAS_W,
  TOUR_SCENES,
  type SceneAction,
  type SceneDefinition,
  type SceneId,
  type SceneVars,
} from '../lib/tourScenes';
import SuggestEditsStage from './tourDemoStages/SuggestEditsStage';
import CommentsStage from './tourDemoStages/CommentsStage';
import GeneralNotesStage from './tourDemoStages/GeneralNotesStage';

interface Props {
  sceneId: SceneId;
  /** Which document the tour is about; picks the scene's variant. */
  kind: ReviewKind;
  reduceMotion: boolean;
  manualPlay: boolean;
  onPlay: () => void;
}

const STAGE_COMPONENTS: Record<
  SceneId,
  (props: { vars: SceneVars; kind: ReviewKind }) => React.JSX.Element
> = {
  'suggest-edits': SuggestEditsStage,
  comments: CommentsStage,
  'general-notes': GeneralNotesStage,
};

interface CursorState {
  x: number;
  y: number;
  ms: number;
  visible: boolean;
}

/**
 * Scene engine: schedules a scene's timeline (src/client/lib/tourScenes.ts)
 * against a fixed 1100x688 canvas scaled to fit its container, and hands the
 * resulting state (vars/cursor/beat/overlays/veil) to the scene's own stage
 * component to render. Ported from mockups/tour-refinement-2.html's inline
 * choreography script.
 *
 * Reduced-motion contract mirrors the video StepMedia it replaces: with
 * prefers-reduced-motion and no opt-in yet, the engine never starts — what
 * renders is simply its initial (reset) state, i.e. the scene's static
 * opening frame — with an accent Play button overlaid. Pressing Play flips
 * `manualPlay`, which starts the timeline exactly like autoplay would have.
 */
export default function TourDemoScene({ sceneId, kind, reduceMotion, manualPlay, onPlay }: Props) {
  const scene = TOUR_SCENES[kind][sceneId];
  const frameRef = useRef<HTMLDivElement>(null);
  const scale = useStageScale(frameRef);
  // Under prefers-reduced-motion, pressing Play runs the same timeline but
  // the global reduced-motion CSS rule keeps transitions collapsed, so the
  // demo renders as a paced sequence of discrete states (typing still ticks
  // char by char via state updates). That's deliberate: the OS preference
  // asks for less motion, and opting in to WATCH the demo isn't opting in to
  // cursor glides and overlay fades. The old <video> only moved smoothly
  // here because prerecorded pixels can't be de-animated.
  const active = !reduceMotion || manualPlay;
  const engine = useSceneEngine(scene, active);
  const showPlayButton = reduceMotion && !manualPlay;
  const StageComponent = STAGE_COMPONENTS[sceneId];

  return (
    // role="img" lives on the animated stage itself, NOT this wrapper: an img
    // role is a leaf in the accessibility tree, so if it wrapped the whole
    // frame the Play/Replay buttons below would be swallowed as presentational
    // and unreachable in screen-reader browse mode. As siblings of the img
    // node they stay first-class interactive elements (and the modal's focus
    // trap already covers them).
    <div ref={frameRef} className="absolute inset-0">
      <div
        className="absolute top-0 left-0 origin-top-left"
        style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${scale})` }}
        role="img"
        aria-label={scene.label}
      >
        <StageComponent vars={engine.vars} kind={kind} />
        <SceneOverlayEnter show={engine.overlayEnter} />
        <SceneOverlayExit show={engine.overlayExit} />
        <SceneCursor cursor={engine.cursor} clickTick={engine.clickTick} />
        <SceneBeat text={engine.beatText} visible={engine.beatVisible} />
      </div>
      {showPlayButton && <PlayButton label={scene.label} onClick={onPlay} />}
      {engine.veil && <ReplayVeil label={scene.label} onClick={engine.play} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage scaling — internal canvas coordinates never change; only the CSS
// transform scale does. Guarded for SSR/no-window and environments without
// ResizeObserver.
// ---------------------------------------------------------------------------

function useStageScale(frameRef: React.RefObject<HTMLDivElement | null>): number {
  const [scale, setScale] = useState(1);

  // useLayoutEffect, not useEffect: the initial measure must land before
  // first paint, or every media-step mount flashes one frame of the 1100px
  // canvas at scale 1 (a zoomed-in top-left crop) before snapping to fit.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el || typeof window === 'undefined') return;
    const update = () => setScale(el.clientWidth / CANVAS_W);
    // Measure once unconditionally BEFORE the ResizeObserver guard: without
    // the observer we lose live re-fitting, but scale must never stay at the
    // seeded 1 (an 1100px canvas clipped inside a ~560px frame).
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frameRef]);

  return scale;
}

// ---------------------------------------------------------------------------
// The engine itself — schedules the scene's timeline via setTimeout, all
// cleared on reset/unmount. A generation counter (mirrors the care taken in
// ClientOnboardingTour's mount effect) guards against React StrictMode's
// dev-only double-mount replay firing stale callbacks after a reset.
// ---------------------------------------------------------------------------

interface EngineResult {
  vars: SceneVars;
  cursor: CursorState;
  clickTick: number;
  beatText: string | null;
  beatVisible: boolean;
  overlayEnter: boolean;
  overlayExit: boolean;
  veil: boolean;
  play: () => void;
}

function useSceneEngine(scene: SceneDefinition, active: boolean): EngineResult {
  const [vars, setVars] = useState<SceneVars>(scene.initialVars);
  const [cursor, setCursor] = useState<CursorState>({ ...scene.initialCursor, ms: 0, visible: false });
  const [clickTick, setClickTick] = useState(0);
  const [beatText, setBeatText] = useState<string | null>(null);
  const [beatVisible, setBeatVisible] = useState(false);
  const [overlayEnter, setOverlayEnter] = useState(false);
  const [overlayExit, setOverlayExit] = useState(false);
  const [veil, setVeil] = useState(false);

  const timersRef = useRef<number[]>([]);
  const genRef = useRef(0);
  // Live typing runs, so the veil can flush them to their final state. The
  // per-char chain is relative timers while everything else is absolute: in a
  // throttled tab (background clamps every timeout to ~1s) the chain falls
  // far behind schedule, and the absolute veil step would otherwise land over
  // half-typed text that keeps mutating underneath it.
  const typeRunsRef = useRef<{ action: Extract<SceneAction, { kind: 'type' }>; done: boolean }[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const reset = useCallback(() => {
    genRef.current += 1;
    clearTimers();
    typeRunsRef.current = [];
    setVars(scene.initialVars);
    setCursor({ ...scene.initialCursor, ms: 0, visible: true });
    setClickTick(0);
    setBeatText(null);
    setBeatVisible(false);
    setOverlayEnter(false);
    setOverlayExit(false);
    setVeil(false);
  }, [scene, clearTimers]);

  const play = useCallback(() => {
    reset();
    const gen = genRef.current;

    const schedule = (delay: number, fn: () => void) => {
      const id = window.setTimeout(() => {
        if (genRef.current !== gen) return;
        fn();
      }, delay);
      timersRef.current.push(id);
    };

    const runType = (action: Extract<SceneAction, { kind: 'type' }>) => {
      const run = { action, done: false };
      typeRunsRef.current.push(run);
      let i = 0;
      const step = () => {
        if (genRef.current !== gen || run.done) return;
        i += 1;
        const slice = action.text.slice(0, i);
        setVars((v) => ({ ...v, [action.key]: slice }));
        const milestone = action.milestones?.find((m) => m.charCount === i);
        if (milestone) {
          setVars((v) => {
            const next = { ...v };
            milestone.sets.forEach((s) => {
              next[s.key] = s.value;
            });
            return next;
          });
        }
        if (i < action.text.length) {
          const id = window.setTimeout(step, action.charMs);
          timersRef.current.push(id);
        } else {
          run.done = true;
        }
      };
      const id = window.setTimeout(step, action.charMs);
      timersRef.current.push(id);
    };

    const applyAction = (action: SceneAction) => {
      switch (action.kind) {
        case 'cursor':
          setCursor({ x: action.x, y: action.y, ms: action.ms, visible: true });
          break;
        case 'click':
          setClickTick((t) => t + 1);
          break;
        case 'cursorHide':
          setCursor((c) => ({ ...c, visible: false, ms: 500 }));
          break;
        case 'beat':
          setBeatVisible(false);
          if (action.text) {
            const text = action.text;
            schedule(180, () => {
              setBeatText(text);
              setBeatVisible(true);
            });
          }
          break;
        case 'overlay':
          if (action.which === 'enter') setOverlayEnter(action.show);
          else setOverlayExit(action.show);
          break;
        case 'veil':
          // The veil marks the scene's end: flush any typing chain that timer
          // throttling left unfinished (see typeRunsRef) so the final frame
          // under the veil is always the completed state, then stop the chain.
          if (action.show) {
            typeRunsRef.current.forEach((run) => {
              if (run.done) return;
              run.done = true;
              setVars((v) => {
                const next = { ...v, [run.action.key]: run.action.text };
                run.action.milestones?.forEach((m) =>
                  m.sets.forEach((s) => {
                    next[s.key] = s.value;
                  }),
                );
                return next;
              });
            });
          }
          setVeil(action.show);
          break;
        case 'set':
          setVars((v) => ({ ...v, [action.key]: action.value }));
          break;
        case 'type':
          runType(action);
          break;
      }
    };

    for (const step of scene.timeline) {
      schedule(step.at, () => applyAction(step.action));
    }
  }, [scene, reset]);

  useEffect(() => {
    if (!active) return;
    play();
    return () => {
      genRef.current += 1;
      clearTimers();
    };
    // scene.id captures a scene change; play/reset are stable per scene via
    // useCallback deps and don't need to be listed separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id, active]);

  return { vars, cursor, clickTick, beatText, beatVisible, overlayEnter, overlayExit, veil, play };
}

// ---------------------------------------------------------------------------
// Shared primitives — cursor dot + ripple, beat caption chip, enter/exit
// overlays, end veil with replay. All opacity-only fades (brand motion
// rule); cursor/typing motion is the one editorial exception.
// ---------------------------------------------------------------------------

function SceneCursor({ cursor, clickTick }: { cursor: CursorState; clickTick: number }) {
  const [clicking, setClicking] = useState(false);
  const prevTick = useRef(clickTick);

  useEffect(() => {
    if (clickTick === prevTick.current) return;
    prevTick.current = clickTick;
    if (clickTick === 0) return;
    setClicking(true);
    const id = window.setTimeout(() => setClicking(false), 500);
    return () => window.clearTimeout(id);
  }, [clickTick]);

  return (
    <div
      className={`absolute top-0 left-0 w-4 h-4 rounded-full bg-accent border-2 border-page shadow-flare z-[60] pointer-events-none transition-[transform,opacity] ease-[cubic-bezier(0.4,0,0.2,1)] ${
        cursor.visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)`, transitionDuration: `${cursor.ms}ms` }}
    >
      {clicking && (
        <span key={clickTick} className="absolute -inset-3.5 rounded-full border-2 border-accent animate-ping" aria-hidden />
      )}
    </div>
  );
}

function SceneBeat({ text, visible }: { text: string | null; visible: boolean }) {
  return (
    <div
      className={`absolute left-3.5 bottom-3 z-[70] font-mono text-[10px] uppercase tracking-[0.1em] font-semibold text-page bg-ink-900/85 px-2.5 py-1.5 rounded-full transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        visible && text ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {text ?? ''}
    </div>
  );
}

function SceneOverlayEnter({ show }: { show: boolean }) {
  return (
    <div
      className={`absolute inset-0 z-50 flex items-center justify-center bg-accent-hover pointer-events-none transition-opacity duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        show ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <span className="font-mono text-[13px] uppercase tracking-[0.22em] font-semibold text-accent-fg">
        // Suggest mode
      </span>
    </div>
  );
}

function SceneOverlayExit({ show }: { show: boolean }) {
  return (
    <div
      className={`absolute inset-0 z-50 bg-page pointer-events-none transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        show ? 'opacity-[0.88]' : 'opacity-0'
      }`}
    />
  );
}

function PlayButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Play ${label}`}
      className="absolute inset-0 z-[80] flex items-center justify-center group"
    >
      <span className="w-12 h-12 rounded-full bg-accent text-accent-fg flex items-center justify-center shadow-flare-lg/30 group-hover:scale-105 transition-transform">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
  );
}

function ReplayVeil({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="absolute inset-0 z-[80] flex items-center justify-center bg-ink-900/28 animate-fade-in">
      <button
        type="button"
        onClick={onClick}
        aria-label={`Replay ${label}`}
        className="w-10 h-10 rounded-full bg-accent text-accent-fg flex items-center justify-center shadow-flare-lg/30 hover:scale-105 transition-transform"
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden>
          <path d="M12 5V2L7 6l5 4V7c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5H5c0 3.87 3.13 7 7 7s7-3.13 7-7-3.13-7-7-7z" />
        </svg>
      </button>
    </div>
  );
}
